import { Check } from 'lucide-react';
import { useState, type ComponentPropsWithoutRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { MediaKind } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useMediaList } from '../hooks/use-media-list';

import { MediaFolderSelect } from './media-folder-select';
import { MediaThumb } from './media-thumb';

const ALL = '__all__';
const PAGE_SIZE = 12;

export interface CompactMediaPickerProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> {
  /** The picked media id; empty or null for none. */
  value: string | null;
  onChange: (mediaId: string) => void;
  /** Kind the filter starts on; the user can switch kinds unless `kindLocked`. */
  initialKind?: MediaKind | undefined;
  kindLocked?: boolean;
  /** Accessible name of the grid; defaults to "Media library". */
  label?: string;
  /** Shown when the library has nothing of the chosen kind. */
  emptyMessage?: string;
}

/**
 * One file from the organisation's media library, picked in place so a dialog or form does not
 * stack a second dialog on top of itself.
 */
export function CompactMediaPicker({
  value,
  onChange,
  initialKind = 'image',
  kindLocked = false,
  label,
  emptyMessage,
  className,
  ...rest
}: CompactMediaPickerProps) {
  const { t } = useTranslation('media');
  const [kind, setKind] = useState<MediaKind | undefined>(initialKind);
  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const query = useMediaList({
    limit: PAGE_SIZE,
    ...(kind ? { kind } : {}),
    ...(folderId ? { folderId } : {}),
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className={cn('flex flex-col gap-3', className)} role="group" {...rest}>
      <div className="flex flex-wrap gap-2">
        {kindLocked ? null : (
          <Select
            value={kind ?? ALL}
            onValueChange={(next) => setKind(next === ALL ? undefined : (next as MediaKind))}
          >
            <SelectTrigger className="w-[160px]" aria-label={t('filters.kind')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('filters.allKinds')}</SelectItem>
              {Object.values(MediaKind).map((option) => (
                <SelectItem key={option} value={option}>
                  {t(`kind.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <MediaFolderSelect value={folderId} onChange={setFolderId} className="w-[180px]" />
      </div>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-md" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
          {emptyMessage ?? t('compactPicker.empty')}
        </p>
      ) : (
        <ul
          aria-label={label ?? t('compactPicker.library')}
          className="grid max-h-72 grid-cols-3 gap-2 overflow-y-auto p-0.5 sm:grid-cols-4"
        >
          {items.map((media) => {
            const selected = media.id === value;
            const name = media.alt ?? t('untitled');
            return (
              <li key={media.id}>
                <button
                  type="button"
                  aria-pressed={selected}
                  aria-label={name}
                  onClick={() => onChange(media.id)}
                  className="group focus-visible:ring-ring/50 relative block w-full rounded-md text-left outline-none focus-visible:ring-[3px]"
                >
                  <MediaThumb
                    media={media}
                    className={cn(
                      'group-hover:border-primary/60 w-full border transition-colors',
                      selected && 'ring-primary ring-2',
                    )}
                  />
                  {selected ? (
                    <span className="bg-primary text-primary-foreground absolute top-1.5 right-1.5 flex size-5 items-center justify-center rounded-full">
                      <Check className="size-3" aria-hidden />
                    </span>
                  ) : null}
                  <span className="mt-1 block truncate text-[11px]" aria-hidden>
                    {name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {query.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-center"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? t('loadingMore') : t('loadMore')}
        </Button>
      ) : null}
    </div>
  );
}
